/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  return localStorage.getItem("lang") || "en";
}

/**
 * Format month display based on language
 */
function formatMonthDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short"
  });
}

// Get language once at module load
const lang = getCurrentLanguage();
const isGerman = lang === "de";

export const facilityManagementData = {
  // Facility Metadata (Based on Munich Commercial Building)
  facility: {
    id: 1,
    name: "TechHub München",
    location: "Munich, Bavaria, Germany",
    address: "Leopoldstraße 252, 80807 München",
    totalArea: 10000, // sqm - typical medium commercial building
    floorsCount: 4,
    floorArea: 2500, // sqm per floor
    totalAssets: 847, // Total equipment/assets being managed
    criticalAssets: 156, // Critical systems requiring 24/7 monitoring
    maintenanceStaff: 12, // On-site and contract staff
    vendorCount: 8, // Service providers
    capacity: 200, // Maximum occupancy

    buildingInfo: {
      yearBuilt: 2020,
      buildingType: isGerman ? "Gewerbebüro" : "Commercial Office",
      constructionType: isGerman
        ? "Moderne energieeffiziente Bauweise"
        : "Modern Energy Efficient",
      certifications: [
        "LEED Gold",
        "BREEAM Excellent",
        "ISO 14001",
        "ISO 50001"
      ],
      hvacSystem: isGerman
        ? "VRF mit Wärmerückgewinnung"
        : "VRF with heat recovery",
      lightingSystem: isGerman
        ? "LED mit Tageslicht-Sensoren"
        : "LED with daylight sensors",
      securitySystem: isGerman
        ? "Zutrittskontrolle + CCTV"
        : "Access control + CCTV",
      elevators: 4,
      parkingSpaces: 120
    },

    operatingHours: {
      start: "06:00", // Building access (cleaning starts early)
      end: "22:00", // Building closes
      coreHours: {
        start: "08:00",
        end: "19:00"
      },
      weekdays: isGerman
        ? ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"]
        : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      weekendAccess: isGerman ? "Nur nach Vereinbarung" : "By appointment only"
    },

    // German Market Rates (2024-2025)
    costs: {
      energyCostPerKwh: 0.28, // € - Real German commercial rate
      waterCostPerM3: 4.5, // € per cubic meter
      wasteDisposalPerKg: 0.15, // € per kg
      maintenanceBudget: 45000, // € monthly
      operatingBudget: 125000 // € monthly (all costs)
    },

    // Environmental Factors
    environmental: {
      co2FactorPerKwh: 0.3, // kg CO₂/kWh (German electricity mix 2024)
      waterEfficiencyTarget: 0.5, // m³ per person per day
      wasteRecyclingTarget: 65, // % recycling rate
      greenSpaceArea: 500 // sqm (roof garden + courtyards)
    },

    // Performance Benchmarks (German Building Standards)
    benchmarks: {
      equipmentUptime: 95, // % minimum uptime target
      energyIntensity: 70, // kWh/sqm (GEG 2020 standard)
      spaceUtilization: 75, // % target utilization
      vendorSLA: 95, // % SLA compliance target
      predictiveAccuracy: 85, // % prediction accuracy target
      responseTime: 4, // hours maximum for non-critical issues
      maintenanceCompletion: 90 // % on-time completion target
    },

    // Asset Categories
    assetCategories: {
      hvac: 245, // Heating, Ventilation, Air Conditioning units
      electrical: 182, // Electrical systems
      plumbing: 156, // Plumbing systems
      elevators: 42, // Elevator components
      security: 98, // Security systems
      lighting: 78, // Lighting systems
      fireSystem: 46 // Fire safety equipment
    },

    // Contact Information
    contacts: {
      facilityManager: "Klaus Müller",
      technicalLead: "Anna Schmidt",
      emergencyContact: "+49 89 1234 5678",
      maintenanceHotline: "+49 89 1234 5679"
    }
  },

  // Historical Monthly Data (22 months: Jan 2024 - Oct 2025)
  monthlyData: [
    // ==================== JANUARY 2024 ====================
    {
      month: "2024-01",
      displayMonth: formatMonthDisplay(new Date(2024, 0, 31)),
      date: "2024-01-31",

      // KPI 1: Equipment & Asset Status
      equipment: {
        uptimePercentage: 94.2,
        changePercent: null, // First month
        activeAssets: 798,
        totalAssets: 847,
        criticalAssets: {
          operational: 148,
          maintenance: 5,
          offline: 3
        },
        maintenanceScheduled: 42,
        maintenanceCompleted: 38,
        maintenanceOverdue: 4,
        assetHealthScore: 87.5,

        // Asset Health by Category
        assetHealth: [
          {
            category: isGerman ? "Lüftung" : "HVAC",
            score: 95,
            count: 245,
            issues: 2
          },
          {
            category: isGerman ? "Elektrik" : "Electrical",
            score: 78,
            count: 182,
            issues: 8
          },
          {
            category: isGerman ? "Sanitär" : "Plumbing",
            score: 65,
            count: 156,
            issues: 12
          },
          {
            category: isGerman ? "Aufzüge" : "Elevators",
            score: 88,
            count: 42,
            issues: 3
          },
          {
            category: isGerman ? "Sicherheit" : "Security",
            score: 92,
            count: 98,
            issues: 1
          },
          {
            category: isGerman ? "Beleuchtung" : "Lighting",
            score: 83,
            count: 78,
            issues: 4
          },
          {
            category: isGerman ? "Brandschutz" : "Fire System",
            score: 96,
            count: 46,
            issues: 1
          }
        ],

        // Top Issues
        topIssues: [
          {
            category: isGerman ? "Lüftungssysteme" : "HVAC Systems",
            count: 12,
            severity: isGerman ? "Mittel" : "Medium",
            avgResolutionTime: 6.2
          },
          {
            category: isGerman ? "Elektrik" : "Electrical",
            count: 8,
            severity: isGerman ? "Niedrig" : "Low",
            avgResolutionTime: 4.1
          },
          {
            category: isGerman ? "Sanitär" : "Plumbing",
            count: 5,
            severity: isGerman ? "Hoch" : "High",
            avgResolutionTime: 12.5
          }
        ],

        // Downtime Statistics
        downtimeHours: 127.4,
        downtimeEvents: 18,
        mtbf: 720, // Mean Time Between Failures (hours)
        mttr: 4.2, // Mean Time To Repair (hours)

        // Work Orders
        workOrders: {
          total: 156,
          completed: 138,
          pending: 12,
          overdue: 6,
          emergency: 8
        },

        // Maintenance Types
        maintenanceBreakdown: {
          preventive: 62, // %
          corrective: 28,
          emergency: 10
        },

        // Uptime Trend (last 30 days)
        uptimeTrend: [
          92, 94, 91, 96, 94, 89, 88, 95, 97, 94, 92, 90, 93, 95, 96, 94, 91,
          88, 92, 94, 96, 93, 91, 89, 94, 95, 97, 94, 92, 94
        ]
      },

      // KPI 2: Energy Management
      energy: {
        totalConsumption: 520000, // kWh (winter - more heating)
        intensity: 52.0, // kWh/sqm
        changePercent: null,
        totalCost: 145600, // € (520000 × 0.28)
        costPerSqm: 14.56,
        peakDemand: 850, // kW
        offPeakUsage: 62, // % of consumption during off-peak
        renewablePercentage: 18, // % from renewable sources

        // Energy Breakdown by System
        breakdown: {
          hvac: 38, // %
          lighting: 28,
          equipment: 22,
          elevators: 7,
          other: 5
        },

        // Energy Breakdown by Space Type
        spaceBreakdown: {
          offices: 45, // %
          common: 25,
          mechanical: 20,
          parking: 10
        },

        // Hourly Usage Pattern (average per hour during operating hours)
        hourlyUsage: [
          { hour: "06:00", consumption: 280 },
          { hour: "07:00", consumption: 350 },
          { hour: "08:00", consumption: 420 },
          { hour: "09:00", consumption: 480 },
          { hour: "10:00", consumption: 520 },
          { hour: "11:00", consumption: 580 },
          { hour: "12:00", consumption: 620 },
          { hour: "13:00", consumption: 640 },
          { hour: "14:00", consumption: 660 },
          { hour: "15:00", consumption: 680 },
          { hour: "16:00", consumption: 640 },
          { hour: "17:00", consumption: 600 },
          { hour: "18:00", consumption: 520 },
          { hour: "19:00", consumption: 450 },
          { hour: "20:00", consumption: 320 },
          { hour: "21:00", consumption: 250 }
        ],

        // Energy Efficiency Score
        efficiencyScore: 82,

        // Comparison with Target
        vsTarget: {
          target: 70, // kWh/sqm annual target
          current: 52, // Current month
          status: "below_target" // Good! Below target
        },

        // Cost Savings (from efficiency measures)
        costSavings: 8200, // € saved this month

        // Next Month Prediction
        predictedConsumption: 510000, // kWh
        predictedCost: 142800 // €
      },

      // KPI 3: Space Utilization
      spaceUtilization: {
        overallRate: 72.5, // %
        changePercent: null,
        occupiedArea: 7250, // sqm
        totalArea: 10000,

        // Zone Breakdown
        zoneBreakdown: [
          {
            zone: isGerman ? "Großraumbüro" : "Open Office",
            utilization: 76,
            area: 3500,
            capacity: 175,
            occupancy: 168,
            peakTime: "14:00-16:00"
          },
          {
            zone: isGerman ? "Besprechungsräume" : "Meeting Rooms",
            utilization: 82,
            area: 1200,
            capacity: 60,
            occupancy: 48,
            peakTime: "10:00-12:00"
          },
          {
            zone: isGerman ? "Konferenzräume" : "Conference Rooms",
            utilization: 68,
            area: 800,
            capacity: 40,
            occupancy: 27,
            peakTime: "09:00-11:00"
          },
          {
            zone: isGerman ? "Gemeinschaftsbereiche" : "Common Areas",
            utilization: 55,
            area: 1500,
            capacity: 75,
            occupancy: 41,
            peakTime: "12:00-14:00"
          },
          {
            zone: isGerman ? "Private Büros" : "Private Offices",
            utilization: 85,
            area: 2000,
            capacity: 50,
            occupancy: 42,
            peakTime: "09:00-17:00"
          },
          {
            zone: isGerman ? "Technische Räume" : "Technical Rooms",
            utilization: 100,
            area: 1000,
            capacity: 0,
            occupancy: 0,
            peakTime: isGerman ? "Durchgehend" : "Continuous"
          }
        ],

        // Peak Usage Times
        peakUsage: {
          weekday: isGerman ? "Mittwoch" : "Wednesday",
          time: "14:00-16:00",
          occupancyRate: 88
        },

        // Average Density
        averageDensity: 31.2, // sqm per person

        // Booking Statistics (for bookable spaces)
        bookingStats: {
          totalBookings: 542,
          avgDuration: 2.3, // hours
          utilizationRate: 76,
          noShowRate: 8 // %
        },

        // Desk Booking (Hot Desking)
        deskBooking: {
          totalDesks: 175,
          booked: 142,
          available: 33,
          utilizationRate: 81
        }
      },

      // KPI 4: Environmental Conditions
      environmental: {
        temperature: {
          average: 21.5, // °C
          min: 18.2,
          max: 24.8,
          zonesOutOfRange: 2, // Number of zones outside comfort range
          comfortScore: 88
        },

        humidity: {
          average: 45, // %
          min: 38,
          max: 52,
          targetRange: "40-60",
          complianceRate: 94
        },

        airQuality: {
          index: 85, // 0-100 scale (higher is better)
          co2Average: 620, // ppm
          particulateMatter: 8, // µg/m³ PM2.5
          vocLevel: 250, // µg/m³
          rating: isGerman ? "Gut" : "Good"
        },

        lighting: {
          averageLux: 520,
          targetLux: 500,
          energyEfficiency: 92,
          daylightHarvesting: 35 // % from natural light
        },

        noise: {
          averageDb: 48,
          targetDb: 50,
          quietZoneCompliance: 96,
          peakNoiseEvents: 12
        },

        // Comfort Complaints
        complaints: {
          temperature: 5,
          airQuality: 2,
          lighting: 3,
          noise: 4,
          total: 14
        },

        // Sustainability Metrics
        sustainability: {
          waterUsage: 2250, // m³ per month
          wasteGenerated: 1850, // kg
          recyclingRate: 62, // %
          co2Emissions: 1560, // kg
          greenSpaceUsage: 78 // % of available green space used
        }
      },

      // KPI 5: Vendor & Service Performance
      vendorPerformance: {
        slaCompliance: 92.5, // %
        changePercent: null,
        activeVendors: 8,
        workOrdersCompleted: 178,
        avgResponseTime: 2.8, // hours
        avgResolutionTime: 18.5, // hours
        satisfactionScore: 4.2, // out of 5

        // Vendor Breakdown
        vendorBreakdown: [
          {
            vendor: isGerman ? "HVAC-Wartung" : "HVAC Maintenance",
            slaCompliance: 95,
            responseTime: 2.1,
            spend: 12500,
            workOrders: 45
          },
          {
            vendor: isGerman ? "Elektrische Dienste" : "Electrical Services",
            slaCompliance: 88,
            responseTime: 3.5,
            spend: 8200,
            workOrders: 32
          },
          {
            vendor: isGerman ? "Sanitärarbeiten" : "Plumbing Services",
            slaCompliance: 90,
            responseTime: 4.2,
            spend: 6800,
            workOrders: 28
          },
          {
            vendor: isGerman ? "Reinigungsdienste" : "Cleaning Services",
            slaCompliance: 96,
            responseTime: 1.5,
            spend: 15000,
            workOrders: 52
          },
          {
            vendor: isGerman ? "Sicherheit" : "Security",
            slaCompliance: 98,
            responseTime: 0.5,
            spend: 18000,
            workOrders: 12
          },
          {
            vendor: isGerman ? "Aufzugswartung" : "Elevator Maintenance",
            slaCompliance: 94,
            responseTime: 1.8,
            spend: 7500,
            workOrders: 8
          },
          {
            vendor: isGerman ? "Landschaftspflege" : "Landscaping",
            slaCompliance: 91,
            responseTime: 24.0,
            spend: 3200,
            workOrders: 4
          },
          {
            vendor: isGerman ? "Abfallmanagement" : "Waste Management",
            slaCompliance: 89,
            responseTime: 12.0,
            spend: 4800,
            workOrders: 8
          }
        ],

        // Total Spend
        totalSpend: 76000, // € per month

        // Top Performers
        topPerformers: [
          isGerman ? "Sicherheit" : "Security",
          isGerman ? "Reinigungsdienste" : "Cleaning Services",
          isGerman ? "HVAC-Wartung" : "HVAC Maintenance"
        ],

        // Issues & Escalations
        issues: {
          total: 8,
          resolved: 6,
          pending: 2,
          escalated: 1
        }
      },

      // KPI 6: Predictive Analytics & Insights
      predictiveAnalytics: {
        failurePredictions: 5, // Predicted failures in next 30 days
        preventedDowntime: 180, // Hours saved by predictive maintenance
        accuracyRate: 87, // % accuracy of predictions
        costSavings: 22500, // € saved through predictive actions

        // High-Risk Assets
        highRiskAssets: [
          {
            asset: isGerman ? "HVAC-Einheit 3B" : "HVAC Unit 3B",
            failureRisk: 78,
            category: isGerman ? "Lüftung" : "HVAC",
            predictedFailureDate: "2024-02-15",
            recommendedAction: isGerman
              ? "Vorbeugende Wartung"
              : "Preventive Maintenance"
          },
          {
            asset: isGerman ? "Aufzug 2" : "Elevator 2",
            failureRisk: 65,
            category: isGerman ? "Aufzüge" : "Elevators",
            predictedFailureDate: "2024-02-22",
            recommendedAction: isGerman
              ? "Komponentenaustausch"
              : "Component Replacement"
          },
          {
            asset: isGerman ? "Hauptwasserleitung" : "Main Water Line",
            failureRisk: 72,
            category: isGerman ? "Sanitär" : "Plumbing",
            predictedFailureDate: "2024-03-05",
            recommendedAction: isGerman ? "Inspektion" : "Inspection"
          }
        ],

        // Optimization Opportunities
        optimizations: [
          {
            area: isGerman ? "Energieverbrauch" : "Energy Consumption",
            potential: isGerman
              ? "15% Reduzierung möglich"
              : "15% reduction possible",
            estimatedSavings: 6800,
            action: isGerman
              ? "HVAC-Zeitplan optimieren"
              : "Optimize HVAC scheduling"
          },
          {
            area: isGerman ? "Raumnutzung" : "Space Utilization",
            potential: isGerman
              ? "20% ungenutzte Kapazität"
              : "20% unused capacity",
            estimatedSavings: 0,
            action: isGerman
              ? "Zonenkonsolidierung in Betracht ziehen"
              : "Consider zone consolidation"
          },
          {
            area: isGerman ? "Wartungsplanung" : "Maintenance Scheduling",
            potential: isGerman ? "25% Zeitersparnis" : "25% time savings",
            estimatedSavings: 3500,
            action: isGerman
              ? "Wartungsfenster konsolidieren"
              : "Consolidate maintenance windows"
          }
        ],

        // Trend Analysis
        trends: {
          energyTrend: isGerman ? "Steigend" : "Increasing",
          utilizationTrend: isGerman ? "Stabil" : "Stable",
          maintenanceTrend: isGerman ? "Verbesserung" : "Improving",
          costTrend: isGerman ? "Steigend" : "Increasing"
        }
      }
    },
    // ==================== FEBRUARY 2024 ====================
    {
      month: "2024-02",
      displayMonth: formatMonthDisplay(new Date(2024, 1, 29)),
      date: "2024-02-29",

      // KPI 1: Equipment & Asset Status
      equipment: {
        uptimePercentage: 94.8,
        changePercent: 0.64,
        activeAssets: 802,
        totalAssets: 847,
        criticalAssets: {
          operational: 150,
          maintenance: 4,
          offline: 2
        },
        maintenanceScheduled: 45,
        maintenanceCompleted: 42,
        maintenanceOverdue: 3,
        assetHealthScore: 88.2,

        // Asset Health by Category
        assetHealth: [
          {
            category: isGerman ? "Lüftung" : "HVAC",
            score: 96,
            count: 245,
            issues: 1
          },
          {
            category: isGerman ? "Elektrik" : "Electrical",
            score: 80,
            count: 182,
            issues: 6
          },
          {
            category: isGerman ? "Sanitär" : "Plumbing",
            score: 68,
            count: 156,
            issues: 10
          },
          {
            category: isGerman ? "Aufzüge" : "Elevators",
            score: 89,
            count: 42,
            issues: 2
          },
          {
            category: isGerman ? "Sicherheit" : "Security",
            score: 93,
            count: 98,
            issues: 1
          },
          {
            category: isGerman ? "Beleuchtung" : "Lighting",
            score: 85,
            count: 78,
            issues: 3
          },
          {
            category: isGerman ? "Brandschutz" : "Fire System",
            score: 97,
            count: 46,
            issues: 0
          }
        ],

        // Top Issues
        topIssues: [
          {
            category: isGerman ? "Lüftungssysteme" : "HVAC Systems",
            count: 10,
            severity: isGerman ? "Mittel" : "Medium",
            avgResolutionTime: 5.8
          },
          {
            category: isGerman ? "Elektrik" : "Electrical",
            count: 7,
            severity: isGerman ? "Niedrig" : "Low",
            avgResolutionTime: 3.9
          },
          {
            category: isGerman ? "Sanitär" : "Plumbing",
            count: 4,
            severity: isGerman ? "Mittel" : "Medium",
            avgResolutionTime: 11.2
          }
        ],

        // Downtime Statistics
        downtimeHours: 114.2,
        downtimeEvents: 15,
        mtbf: 745,
        mttr: 3.9,

        // Work Orders
        workOrders: {
          total: 162,
          completed: 145,
          pending: 11,
          overdue: 6,
          emergency: 6
        },

        // Maintenance Types
        maintenanceBreakdown: {
          preventive: 65,
          corrective: 26,
          emergency: 9
        },

        // Uptime Trend (last 30 days)
        uptimeTrend: [
          93, 95, 92, 97, 95, 90, 89, 96, 98, 95, 93, 91, 94, 96, 97, 95, 92,
          89, 93, 95, 97, 94, 92, 90, 95, 96, 98, 95, 93, 95
        ]
      },

      // KPI 2: Energy Management
      energy: {
        totalConsumption: 510000,
        intensity: 51.0,
        changePercent: -1.92,
        totalCost: 142800,
        costPerSqm: 14.28,
        peakDemand: 830,
        offPeakUsage: 64,
        renewablePercentage: 19,

        breakdown: {
          hvac: 37,
          lighting: 28,
          equipment: 23,
          elevators: 7,
          other: 5
        },

        spaceBreakdown: {
          offices: 44,
          common: 26,
          mechanical: 20,
          parking: 10
        },

        hourlyUsage: [
          { hour: "06:00", consumption: 270 },
          { hour: "07:00", consumption: 340 },
          { hour: "08:00", consumption: 410 },
          { hour: "09:00", consumption: 470 },
          { hour: "10:00", consumption: 510 },
          { hour: "11:00", consumption: 570 },
          { hour: "12:00", consumption: 610 },
          { hour: "13:00", consumption: 630 },
          { hour: "14:00", consumption: 650 },
          { hour: "15:00", consumption: 670 },
          { hour: "16:00", consumption: 630 },
          { hour: "17:00", consumption: 590 },
          { hour: "18:00", consumption: 510 },
          { hour: "19:00", consumption: 440 },
          { hour: "20:00", consumption: 310 },
          { hour: "21:00", consumption: 240 }
        ],

        efficiencyScore: 84,

        vsTarget: {
          target: 70,
          current: 51,
          status: "below_target"
        },

        costSavings: 8900,
        predictedConsumption: 485000,
        predictedCost: 135800
      },

      // KPI 3: Space Utilization
      spaceUtilization: {
        overallRate: 74.2,
        changePercent: 2.34,
        occupiedArea: 7420,
        totalArea: 10000,

        // Zone Breakdown
        zoneBreakdown: [
          {
            zone: isGerman ? "Großraumbüro" : "Open Office",
            utilization: 78,
            area: 3500,
            capacity: 175,
            occupancy: 172,
            peakTime: "14:00-16:00"
          },
          {
            zone: isGerman ? "Besprechungsräume" : "Meeting Rooms",
            utilization: 84,
            area: 1200,
            capacity: 60,
            occupancy: 50,
            peakTime: "10:00-12:00"
          },
          {
            zone: isGerman ? "Gemeinschaftsbereiche" : "Common Areas",
            utilization: 67,
            area: 2800,
            capacity: 140,
            occupancy: 94,
            peakTime: "12:00-13:00"
          },
          {
            zone: isGerman ? "Lager/Einrichtungen" : "Storage/Facilities",
            utilization: 60,
            area: 2500,
            capacity: 0,
            occupancy: 0,
            peakTime: "N/A"
          }
        ],

        floorUtilization: [
          { floor: 1, rate: 80, area: 2500 },
          { floor: 2, rate: 76, area: 2500 },
          { floor: 3, rate: 72, area: 2500 },
          { floor: 4, rate: 69, area: 2500 }
        ],

        averageDensity: 31.6,
        peakOccupancyTime: "14:00-16:00",
        underutilizedSpaces: 2,

        hotDesking: {
          available: 120,
          utilized: 91,
          efficiency: 75.8
        },

        meetingRooms: {
          total: 24,
          booked: 21,
          utilizationRate: 88
        },

        spaceTypes: {
          workstations: 45,
          meeting: 18,
          collaborative: 15,
          breakout: 12,
          support: 10
        }
      },

      environmental: {
        temperature: {
          average: 21.5,
          min: 19.2,
          max: 24.0,
          targetRange: "20-24",
          compliance: 95,
          zones: [
            { zone: "Floor 1", avg: 21.9, compliance: 97 },
            { zone: "Floor 2", avg: 21.4, compliance: 96 },
            { zone: "Floor 3", avg: 21.3, compliance: 93 },
            { zone: "Floor 4", avg: 21.7, compliance: 94 }
          ]
        },

        humidity: {
          average: 47,
          min: 41,
          max: 54,
          targetRange: "40-60",
          compliance: 97,
          zones: [
            { zone: "Floor 1", avg: 48, compliance: 98 },
            { zone: "Floor 2", avg: 46, compliance: 97 },
            { zone: "Floor 3", avg: 47, compliance: 96 },
            { zone: "Floor 4", avg: 46, compliance: 97 }
          ]
        },

        co2Levels: {
          average: 665,
          max: 890,
          targetMax: 1000,
          compliance: 99,
          zones: [
            { zone: "Floor 1", avg: 705, max: 930, compliance: 98 },
            { zone: "Floor 2", avg: 645, max: 870, compliance: 99 },
            { zone: "Floor 3", avg: 655, max: 880, compliance: 99 },
            { zone: "Floor 4", avg: 655, max: 900, compliance: 99 }
          ]
        },

        airQuality: {
          index: 84,
          rating: "Good",
          pm25: 7.8,
          pm10: 14.6,
          voc: 210,
          zones: [
            { zone: "Floor 1", aqi: 82, pm25: 8.5 },
            { zone: "Floor 2", aqi: 86, pm25: 7.4 },
            { zone: "Floor 3", aqi: 85, pm25: 7.6 },
            { zone: "Floor 4", aqi: 83, pm25: 7.7 }
          ]
        },

        waterUsage: {
          total: 820,
          perPerson: 0.46,
          target: 0.5,
          status: "within_target"
        },

        waste: {
          total: 1150,
          recycled: 700,
          recyclingRate: 61,
          target: 65
        },

        alerts: 2,
        violations: 0,
        alertTypes: [
          { type: "Low Humidity", count: 1, severity: "Low" },
          { type: "CO₂ Spike", count: 1, severity: "Low" }
        ]
      },

      vendorPerformance: {
        slaCompliance: 93.2,
        changePercent: 0.76,
        activeVendors: 8,
        workOrdersCompleted: 162,
        workOrdersTotal: 174,
        completionRate: 93.1,
        avgResponseTime: 2.6,
        avgCompletionTime: 17.8,
        slaBreaches: 11,
        totalSpend: 39400,
        budgetUtilization: 87.6,
        costEfficiency: 89,

        topVendors: [
          {
            name: "HVAC Pro GmbH",
            category: "HVAC",
            sla: 97,
            cost: 13200,
            workOrders: 44,
            rating: 4.7,
            responseTime: 2.1
          },
          {
            name: "Electro Services Munich",
            category: "Electrical",
            sla: 92,
            cost: 8600,
            workOrders: 36,
            rating: 4.4,
            responseTime: 2.9
          },
          {
            name: "CleanTech Solutions",
            category: "Cleaning",
            sla: 95,
            cost: 6400,
            workOrders: 29,
            rating: 4.6,
            responseTime: 1.7
          },
          {
            name: "Security Plus",
            category: "Security",
            sla: 98,
            cost: 4900,
            workOrders: 19,
            rating: 4.9,
            responseTime: 0.4
          }
        ],

        vendorByCategory: {
          hvac: 2,
          electrical: 2,
          plumbing: 1,
          cleaning: 1,
          security: 1,
          landscaping: 1
        },

        ratings: {
          quality: 4.5,
          timeliness: 4.3,
          communication: 4.4,
          cost: 4.2
        }
      },

      predictiveAnalytics: {
        failurePredictions: 6,
        preventedDowntime: 156,
        accuracyRate: 86,
        costSavings: 20400,

        upcomingMaintenance: [
          {
            asset: "Boiler System",
            assetId: "HVAC-BOIL-001",
            date: "2024-03-12",
            probability: 72,
            cost: 3800,
            severity: "Medium",
            recommendedAction: "Annual inspection and tune-up",
            leadTime: 10
          },
          {
            asset: "Elevator A Cable",
            assetId: "ELEV-A-CBL",
            date: "2024-03-18",
            probability: 68,
            cost: 2600,
            severity: "Medium",
            recommendedAction: "Cable inspection and lubrication",
            leadTime: 7
          },
          {
            asset: "Fire Pump",
            assetId: "FIRE-PUMP-001",
            date: "2024-04-02",
            probability: 79,
            cost: 4900,
            severity: "High",
            recommendedAction: "Full pump test and service",
            leadTime: 18
          }
        ],

        riskScore: 25,
        riskLevel: "Low",
        riskFactors: [
          { factor: "Equipment Age", score: 33, impact: "Medium" },
          { factor: "Usage Intensity", score: 25, impact: "Low" },
          { factor: "Environmental", score: 20, impact: "Low" }
        ],

        recommendations: 5,
        recommendationsList: [
          {
            priority: "High",
            area: "HVAC",
            action: "Replace aging boiler before winter",
            estimatedSavings: 12000
          },
          {
            priority: "Medium",
            area: "Lighting",
            action: "Upgrade to LED in remaining areas",
            estimatedSavings: 5400
          },
          {
            priority: "Low",
            area: "Space",
            action: "Implement flexible workspace in Floor 4",
            estimatedSavings: 2800
          }
        ],

        lifeCycleOptimization: 78,
        replacementSchedule: [
          { asset: "Parking Ventilation", dueDate: "2024-07-10", cost: 15000 },
          { asset: "Lobby HVAC", dueDate: "2024-10-15", cost: 22000 }
        ],

        costForecast: {
          nextMonth: 38100,
          nextQuarter: 110000,
          nextYear: 470000,
          confidence: 88
        }
      }
    }
    // Additional months would follow the same pattern with translated fields
  ],

  getCurrentMonthData() {
    return this.monthlyData[this.monthlyData.length - 1];
  },

  getDataByMonth(monthString) {
    return this.monthlyData.find((m) => m.month === monthString);
  },

  getDateRange() {
    return {
      start: this.monthlyData[0].date,
      end: this.monthlyData[this.monthlyData.length - 1].date
    };
  }
};

/**
 * Get seasonal adjustment factor for energy/maintenance
 */
export function getSeasonalFactor(month) {
  const factors = {
    0: 1.15, // January - Winter heating
    1: 1.12, // February - Winter heating
    2: 1.05, // March - Transitional
    3: 0.98, // April - Spring
    4: 0.95, // May - Mild
    5: 1.08, // June - AC starts
    6: 1.12, // July - Peak AC
    7: 1.1, // August - Peak AC
    8: 1.05, // September - Peak AC reducing
    9: 0.98, // October - Mild
    10: 1.05, // November - Heating starts
    11: 1.08 // December - Winter issues
  };
  return factors[month] || 1.0;
}

/**
 * Generate smart month data based on previous month
 */
export function generateSmartMonthData(previousMonth, targetDate) {
  const month = targetDate.getMonth();
  const year = targetDate.getFullYear();

  const baseTrend = 1 + (Math.random() * 0.04 - 0.02); // ±2% random
  const seasonalFactor = getSeasonalFactor(month);

  // Energy increases in winter and summer (HVAC)
  const energyFactor = seasonalFactor;

  // Space utilization drops in summer (vacation)
  const isVacation = month === 6 || month === 7; // July, August
  const utilizationFactor = isVacation ? 0.88 : 1.0;

  const monthString = `${year}-${String(month + 1).padStart(2, "0")}`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${lastDay}`;
  const displayMonth = formatMonthDisplay(targetDate);

  const prev = previousMonth;

  // Calculate new values with trends
  const newUptime = Math.min(
    99,
    parseFloat((prev.equipment.uptimePercentage * baseTrend).toFixed(1))
  );
  const newIntensity = parseFloat(
    (prev.energy.intensity * energyFactor * baseTrend).toFixed(1)
  );
  const newUtilization = parseFloat(
    (prev.spaceUtilization.overallRate * baseTrend * utilizationFactor).toFixed(
      1
    )
  );
  const newSLA = Math.min(
    99,
    parseFloat((prev.vendorPerformance.slaCompliance * baseTrend).toFixed(1))
  );

  return {
    month: monthString,
    displayMonth: displayMonth,
    date: dateString,

    equipment: {
      ...prev.equipment,
      uptimePercentage: newUptime,
      changePercent: parseFloat(
        (
          ((newUptime - prev.equipment.uptimePercentage) /
            prev.equipment.uptimePercentage) *
          100
        ).toFixed(2)
      ),
      activeAssets: Math.min(
        847,
        prev.equipment.activeAssets + Math.round(Math.random() * 6 - 2)
      ),
      assetHealthScore: parseFloat((newUptime * 0.94).toFixed(1))
    },

    energy: {
      ...prev.energy,
      totalConsumption: Math.round(newIntensity * 10000),
      intensity: newIntensity,
      changePercent: parseFloat(
        (
          ((newIntensity - prev.energy.intensity) / prev.energy.intensity) *
          100
        ).toFixed(2)
      ),
      totalCost: Math.round(newIntensity * 10000 * 0.28),
      costPerSqm: parseFloat((newIntensity * 0.28).toFixed(2))
    },

    spaceUtilization: {
      ...prev.spaceUtilization,
      overallRate: newUtilization,
      changePercent: parseFloat(
        (
          ((newUtilization - prev.spaceUtilization.overallRate) /
            prev.spaceUtilization.overallRate) *
          100
        ).toFixed(2)
      ),
      occupiedArea: Math.round(newUtilization * 100),
      averageDensity: parseFloat((newUtilization * 0.43).toFixed(1))
    },

    environmental: {
      ...prev.environmental,
      temperature: {
        ...prev.environmental.temperature,
        average: parseFloat((21 + Math.random() * 2).toFixed(1))
      },
      airQuality: {
        ...prev.environmental.airQuality,
        index: Math.round(80 + Math.random() * 12)
      }
    },

    vendorPerformance: {
      ...prev.vendorPerformance,
      slaCompliance: newSLA,
      changePercent: parseFloat(
        (
          ((newSLA - prev.vendorPerformance.slaCompliance) /
            prev.vendorPerformance.slaCompliance) *
          100
        ).toFixed(2)
      ),
      workOrdersCompleted: Math.round(170 + Math.random() * 30),
      avgResponseTime: parseFloat((2.0 + Math.random() * 1.2).toFixed(1))
    },

    predictiveAnalytics: {
      ...prev.predictiveAnalytics,
      failurePredictions: Math.round(3 + Math.random() * 7),
      preventedDowntime: Math.round(150 + Math.random() * 70),
      accuracyRate: Math.round(85 + Math.random() * 8),
      costSavings: Math.round(18000 + Math.random() * 15000)
    }
  };
}

/**
 * Main export function that auto-generates missing months
 */
export function getFacilityManagementData() {
  const today = new Date();
  let data = [...facilityManagementData.monthlyData];

  // Auto-generate missing months
  const lastDataDate = new Date(data[data.length - 1].date);
  let currentGenDate = new Date(lastDataDate);
  currentGenDate.setMonth(currentGenDate.getMonth() + 1);
  currentGenDate.setDate(1);

  while (currentGenDate < today) {
    const nextMonthStart = new Date(currentGenDate);
    nextMonthStart.setMonth(nextMonthStart.getMonth() + 1);

    if (nextMonthStart > today) break;

    const lastGenerated = data[data.length - 1];
    const newMonth = generateSmartMonthData(lastGenerated, currentGenDate);
    data.push(newMonth);

    currentGenDate.setMonth(currentGenDate.getMonth() + 1);
  }

  // Interpolate current partial month if needed
  const lastMonth = data[data.length - 1];
  const lastMonthDate = new Date(lastMonth.date);

  if (
    lastMonthDate.getMonth() === today.getMonth() &&
    lastMonthDate.getFullYear() === today.getFullYear()
  ) {
    // Current month already exists, interpolate it
    data[data.length - 1] = interpolatePartialMonth(lastMonth, today);
  } else if (lastMonthDate < today) {
    // Need to add current month
    const currentMonth = generateSmartMonthData(lastMonth, today);
    const interpolated = interpolatePartialMonth(currentMonth, today);
    data.push(interpolated);
  }

  return {
    ...facilityManagementData,
    monthlyData: data,
    getCurrentMonthData() {
      return data[data.length - 1];
    },
    getDataByMonth(monthString) {
      return data.find((m) => m.month === monthString);
    },
    getDateRange() {
      return {
        start: data[0].date,
        end: data[data.length - 1].date
      };
    }
  };
}

/**
 * Interpolate partial month data
 */
function interpolatePartialMonth(lastMonthData, currentDate) {
  const lastDataDate = new Date(lastMonthData.date);

  // Check if this is the same month
  if (
    lastDataDate.getMonth() !== currentDate.getMonth() ||
    lastDataDate.getFullYear() !== currentDate.getFullYear()
  ) {
    return lastMonthData;
  }

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const currentDay = currentDate.getDate();
  const progress = currentDay / daysInMonth;

  return {
    ...lastMonthData,
    date: currentDate.toISOString().split("T")[0],
    displayMonth: formatMonthDisplay(currentDate),
    daysInMonth: currentDay,
    isPartial: true,

    // Pro-rate certain metrics
    energy: {
      ...lastMonthData.energy,
      totalConsumption: Math.round(
        lastMonthData.energy.totalConsumption * progress
      ),
      totalCost: Math.round(lastMonthData.energy.totalCost * progress)
    },

    vendorPerformance: {
      ...lastMonthData.vendorPerformance,
      workOrdersCompleted: Math.round(
        lastMonthData.vendorPerformance.workOrdersCompleted * progress
      ),
      totalSpend: Math.round(
        lastMonthData.vendorPerformance.totalSpend * progress
      )
    },

    predictiveAnalytics: {
      ...lastMonthData.predictiveAnalytics,
      preventedDowntime: Math.round(
        lastMonthData.predictiveAnalytics.preventedDowntime * progress
      ),
      costSavings: Math.round(
        lastMonthData.predictiveAnalytics.costSavings * progress
      )
    }
  };
}

// Default export
export default {
  facilityManagementData,
  getFacilityManagementData,
  getSeasonalFactor,
  generateSmartMonthData
};
